/**
 * src/pages/Login.jsx
 * Acceso al panel admin por usuario/contraseña (JWT).
 * El token maestro estático ya NO se introduce ni se guarda en el navegador:
 * el panel se autentica con el JWT de sesión de un usuario con rol 'admin'.
 *
 * Diseño: identidad de marca CM Monitor (Oxford / Coral / Outfit).
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import { api } from '../api/client'

export default function Login() {
  const [email, setEmail]       = useState('')
  const [password, setPassword] = useState('')
  const [error, setError]       = useState('')
  const [loading, setLoading]   = useState(false)
  const setJwt                  = useAuthStore(state => state.setJwt)
  const navigate                = useNavigate()

  async function handleSubmit(e) {
    e.preventDefault()
    if (!email.trim() || !password) {
      setError('Ingresa tu email y contraseña')
      return
    }

    setLoading(true)
    setError('')

    try {
      const { data } = await api.post('/auth/login', {
        email: email.trim(),
        password,
      })
      if (data.rol !== 'admin') {
        setError('Esta cuenta no tiene permisos de administrador.')
        return
      }
      setJwt(data.access_token, {
        email: data.email, nombre: data.nombre, rol: data.rol,
      })
      navigate('/', { replace: true })
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Email o contraseña incorrectos.')
      } else if (err.response?.status === 403) {
        setError('Cuenta desactivada.')
      } else if (err.code === 'ERR_NETWORK' || err.isNetworkError) {
        setError('No se puede conectar al servidor.')
      } else {
        setError('Error al conectar: ' + (err.message || 'desconocido'))
      }
    } finally {
      setLoading(false)
    }
  }

  const inputStyle = (hasError) => ({
    width: '100%',
    background: 'var(--color-surface-2)',
    border: `1px solid ${hasError ? 'var(--color-error)' : 'var(--color-border)'}`,
    borderRadius: '8px',
    padding: '10px 14px',
    fontSize: '14px',
    fontFamily: 'var(--font-ui)',
    color: 'var(--color-text)',
    outline: 'none',
    transition: 'border-color 0.15s',
    boxSizing: 'border-box',
  })

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--color-bg)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
    }}>
      {/* Background grid pattern */}
      <div style={{
        position: 'fixed', inset: 0, opacity: 0.04,
        backgroundImage: 'linear-gradient(var(--color-accent) 1px, transparent 1px), linear-gradient(90deg, var(--color-accent) 1px, transparent 1px)',
        backgroundSize: '40px 40px',
        pointerEvents: 'none',
      }} />

      <div style={{ width: '100%', maxWidth: '400px', position: 'relative' }}>
        {/* Logo / Branding */}
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '11px',
            marginBottom: '8px',
          }}>
            <div style={{
              width: '34px', height: '34px', borderRadius: '7px',
              background: 'var(--color-accent)', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
            }}>
              <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
              </svg>
            </div>
            <span style={{
              fontFamily: 'var(--font-brand)', fontWeight: 700,
              fontSize: '20px', color: 'var(--color-text)',
              letterSpacing: '-0.02em',
            }}>
              CM Monitor
            </span>
          </div>
          <p style={{
            fontSize: '11px', color: 'var(--color-text-3)',
            fontFamily: 'var(--font-ui)', letterSpacing: '0.1em',
            textTransform: 'uppercase',
          }}>
            Convenio Marco · Mercado Público
          </p>
        </div>

        {/* Card */}
        <div style={{
          background: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: '12px',
          padding: '32px',
          boxShadow: 'var(--shadow-lg)',
        }}>
          <h1 style={{
            fontFamily: 'var(--font-brand)',
            fontSize: '21px', fontWeight: 600, color: 'var(--color-text)',
            marginBottom: '4px', letterSpacing: '-0.01em',
          }}>
            Acceso al panel
          </h1>
          <p style={{
            fontSize: '13px', color: 'var(--color-text-2)',
            marginBottom: '28px',
          }}>
            Ingresa con tu cuenta de administrador
          </p>

          <form onSubmit={handleSubmit}>
            {/* Email */}
            <div style={{ marginBottom: '14px' }}>
              <label style={{
                display: 'block', fontSize: '12px', fontWeight: 600,
                color: 'var(--color-text-2)', marginBottom: '6px',
                fontFamily: 'var(--font-ui)', letterSpacing: '0.03em',
              }}>
                EMAIL
              </label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="admin@cmmonitor.cl"
                autoComplete="username"
                disabled={loading}
                style={inputStyle(error)}
                onFocus={e => { e.target.style.borderColor = 'var(--color-accent)' }}
                onBlur={e => { e.target.style.borderColor = error ? 'var(--color-error)' : 'var(--color-border)' }}
              />
            </div>

            {/* Password */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block', fontSize: '12px', fontWeight: 600,
                color: 'var(--color-text-2)', marginBottom: '6px',
                fontFamily: 'var(--font-ui)', letterSpacing: '0.03em',
              }}>
                CONTRASEÑA
              </label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••••••"
                autoComplete="current-password"
                disabled={loading}
                style={inputStyle(error)}
                onFocus={e => { e.target.style.borderColor = 'var(--color-accent)' }}
                onBlur={e => { e.target.style.borderColor = error ? 'var(--color-error)' : 'var(--color-border)' }}
              />
            </div>

            {/* Error */}
            {error && (
              <div style={{
                background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
                borderRadius: '6px', padding: '10px 12px', marginBottom: '16px',
                fontSize: '12px', color: 'var(--color-error)',
                fontFamily: 'var(--font-ui)',
              }}>
                {error}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                background: loading ? 'var(--color-accent-dim)' : 'var(--color-accent)',
                color: '#fff',
                border: 'none', borderRadius: '8px',
                padding: '11px', fontSize: '14px', fontWeight: 700,
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.15s',
                fontFamily: 'var(--font-ui)',
                letterSpacing: '0.01em',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              }}
              onMouseEnter={e => { if (!loading) e.currentTarget.style.background = 'var(--color-accent-2)' }}
              onMouseLeave={e => { if (!loading) e.currentTarget.style.background = 'var(--color-accent)' }}
            >
              {loading ? (
                <>
                  <span style={{
                    width: '14px', height: '14px', border: '2px solid currentColor',
                    borderTopColor: 'transparent', borderRadius: '50%',
                    display: 'inline-block', animation: 'spin 0.7s linear infinite',
                  }} />
                  Verificando...
                </>
              ) : 'Acceder'}
            </button>
          </form>
        </div>

        {/* Footer */}
        <p style={{
          textAlign: 'center', marginTop: '20px', fontSize: '11px',
          color: 'var(--color-text-3)', fontFamily: 'var(--font-ui)',
        }}>
          CM Monitor · Uso interno
        </p>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        input::placeholder { color: var(--color-text-3); }
      `}</style>
    </div>
  )
}
